import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Button, Descriptions, Flex, Input, Modal, Progress, Select, Space, Spin,
    Statistic, Table, Tag, Typography, message,
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import CustomCard from '../../components/Card';
import OneApiService from '../../APIServices/OneApiService';
import {
    money, data, dateTime, statusOf, refundErrorText,
} from './oneHelpers';

const { Title, Text, Paragraph } = Typography;

/**
 * YouGuide One orders, and the refund action.
 *
 * COST AND MARGIN ARE ON THIS PAGE DELIBERATELY. Per-megabyte selling prices
 * from a rate card that can move, and the whole commercial risk of the model is
 * that a country quietly starts losing money. Showing margin per order is what
 * makes that visible within a day instead of at month end.
 */
export default function OneOrders() {
    const [orders, setOrders] = useState([]);
    /** Sold, used, what Telna will bill, and what that leaves — across every
     *  eSIM, because that is the only level at which consumption billing is
     *  legible. See the cards below. */
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * PAYMENT COMPLETED BY DEFAULT.
     *
     * An order whose money is in reads `paid`, `partial` or `completed` —
     * fulfilment rewrites the status the moment it runs — so the useful
     * default is all three, not `paid` alone, which hides every order that
     * was actually delivered. Awaiting-payment and failed orders are noise on
     * the page an operator opens to see what has sold; they are one click
     * away in the filter.
     */
    const PAID_STATES = 'paid,partial,completed';
    const [status, setStatus] = useState(PAID_STATES);
    const [kind, setKind] = useState(null);
    const [query, setQuery] = useState('');

    const [selected, setSelected] = useState(null);
    const [refunding, setRefunding] = useState(false);

    // The store currency's symbol. Read from the server rather than typed
    // here: this page printed '$' in front of figures that have always been
    // in the store currency.
    const [symbol, setSymbol] = useState('\u20ac');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setOrders(await OneApiService.listOrders({
                status: status || undefined,
                kind: kind || undefined,
                q: query.trim() || undefined,
            }));
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [status, kind, query]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        OneApiService.esimStats('telna')
            .then(setStats)
            // A totals failure must not take the orders table with it.
            .catch(() => setStats(null));
    }, []);

    useEffect(() => {
        OneApiService.getSettings()
            .then((st) => { if (st?.currencySymbol) setSymbol(st.currencySymbol); })
            // Losing the symbol costs a label, not the page. The default is
            // the euro sign because that is what the store is configured in.
            .catch(() => {});
    }, []);

    const openOrder = async (orderNumber) => {
        try {
            setSelected(await OneApiService.getOrder(orderNumber));
        } catch (e) {
            message.error(e.message);
        }
    };

    const confirmRefund = (order) => {
        let note = '';

        Modal.confirm({
            title: `Refund ${order.orderNumber}?`,
            width: 560,
            content: (
                <div>
                    <Paragraph style={{ marginTop: 12 }}>
                        <Text strong>{money(order.price, order.currencySymbol)}</Text> will be credited to the
                        customer&apos;s YouGuide One wallet. It is not returned to their card.
                    </Paragraph>

                    {order.line?.hasActivationCode && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message="The eSIM has already been delivered"
                            description={
                                'Telna has no refund call and an activation code cannot be '
                                + 'taken back. This refund absorbs the cost of a profile the '
                                + 'customer keeps.'
                            }
                        />
                    )}

                    <Input.TextArea
                        rows={2}
                        placeholder="Reason (kept on the ledger entry)"
                        onChange={(e) => { note = e.target.value; }}
                    />
                </div>
            ),
            okText: 'Refund to wallet',
            okButtonProps: { danger: true },
            onOk: async () => {
                setRefunding(true);
                try {
                    const result = await OneApiService.refundOrder(order.orderNumber, { note });
                    message.success(
                        `${money(result.refunded, result.currencySymbol)} credited to the customer's wallet`,
                    );
                    setSelected(null);
                    await load();
                } catch (e) {
                    // Each refusal is a different next step for the operator, so
                    // the code is translated rather than the raw message shown.
                    message.error(refundErrorText(e));
                    throw e;
                } finally {
                    setRefunding(false);
                }
            },
        });
    };

    const columns = [
        {
            title: 'Order',
            dataIndex: 'orderNumber',
            // A Button, not an <a> without an href: this opens a modal rather
            // than navigating, and a bare anchor is unreachable by keyboard.
            render: (v) => (
                <Button type="link" style={{ padding: 0 }} onClick={() => openOrder(v)}>
                    {v}
                </Button>
            ),
        },
        {
            title: 'Customer',
            dataIndex: 'email',
            render: (v, r) => (v
                ? (
                    <div>
                        <Text style={{ fontSize: 13 }}>{v}</Text>
                        {!r.userId && (
                            <div><Text type="secondary" style={{ fontSize: 11 }}>guest</Text></div>
                        )}
                    </div>
                )
                : <Text type="secondary">guest, no address</Text>),
        },
        {
            title: 'What',
            render: (_, r) => (r.kind === 'wallet_topup'
                ? <Text>Wallet credit</Text>
                // `totalMb` comes from the eSIM LINE; `mb` is null on a cart
                // order, which is what left this column blank for every sale.
                : <Text>{r.countryName || '—'} · {data(r.totalMb || r.mb)}</Text>),
        },
        {
            title: 'Data',
            key: 'data',
            render: (_, r) => {
                if (r.kind === 'wallet_topup' || !r.totalMb) return <Text type="secondary">—</Text>;
                const pct = Math.round((r.usedMb / r.totalMb) * 100);
                return (
                    <div style={{ minWidth: 140 }}>
                        <Progress percent={pct} size="small" status={r.remainingMb <= 0 ? 'exception' : 'active'} />
                        <Text style={{ fontSize: 11 }}>
                            {data(r.usedMb)} used · <b>{data(r.remainingMb)} left</b>
                        </Text>
                    </div>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (v) => {
                const s = statusOf(v);
                return <Tag color={s.colour}>{s.label}</Tag>;
            },
        },
        {
            title: 'Charged',
            dataIndex: 'price',
            align: 'right',
            // NOT `render: money`. antd calls render(value, record, index), so
            // passing the helper bare handed it the whole ROW as the currency
            // symbol and printed "[object Object]0.00" in every charged cell.
            render: (v, r) => money(v, r.currencySymbol),
        },
        {
            title: 'Cost',
            dataIndex: 'costUsd',
            align: 'right',
            render: (v, r) => (r.kind === 'wallet_topup' ? '—' : money(v, r.currencySymbol)),
        },
        // MARGIN PER ORDER IS REMOVED, and deliberately not replaced in this
        // table. Telna bills CONSUMPTION, so a single order's "margin" is
        // unknowable until the customer has finished travelling — and a
        // customer who never travels costs us nothing, which showed as a
        // large loss the moment the eSIM was funded. The honest view is the
        // total across every eSIM, which is what the cards above now give.
        {
            title: 'From credit',
            dataIndex: 'walletApplied',
            align: 'right',
            render: (v, r) => (Number(v) > 0 ? money(v, r.currencySymbol) : '—'),
        },
        { title: 'Placed', dataIndex: 'createdAt', render: dateTime },
    ];



    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Flex justify="space-between" align="center" wrap gap={12}>
                <div>
                    <Title level={4} style={{ margin: 0 }}>Orders</Title>
                    <Text type="secondary">
                        Every purchase made in the app or on /new-esims.
                    </Text>
                </div>
                <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
                    Refresh
                </Button>
            </Flex>

            {error && <Alert type="error" showIcon message={error} />}

            {/* THE BELOW-COST ALERT IS GONE WITH THE MARGIN COLUMN.
                It compared an order's price against the whole allowance we
                funded, so every per-megabyte eSIM looked like a loss the
                moment it was provisioned — and a customer who never travels,
                the most profitable kind, looked like the worst. Telna bills
                consumption; the totals above are where that is legible. */}
            {stats?.unfunded > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    message={`${stats.unfunded} eSIM${stats.unfunded === 1 ? '' : 's'} sold but never funded`}
                    description={
                        'Their top-up did not land, so the customer has a profile with no '
                        + 'data on it. These are the ones worth acting on first.'
                    }
                />
            )}

            {/* Three zeroes say nothing. Until there is a sale, the useful
                thing on this page is WHY there is not one yet — so the totals
                appear with the orders they summarise. */}
            {stats && stats.esims > 0 ? (
                <Flex gap={16} wrap>
                    <StatCard title="eSIMs sold" value={stats.esims} />
                    <StatCard
                        title="Data used"
                        value={`${data(stats.usedMb)} of ${data(stats.soldMb)}`}
                        raw
                    />
                    {/* WHAT TELNA WILL BILL, not what we set aside. Telna
                        charges consumption, so this is usage x the rate the
                        line was funded at — a customer who never travels
                        costs us nothing, and this is the number that says
                        so. */}
                    <StatCard
                        title="Telna will cost us"
                        value={`$${Number(stats.estimatedCostUsd).toFixed(2)}`}
                        raw
                    />
                    <StatCard
                        title="Charged to customers"
                        value={stats.revenue}
                        symbol={symbol}
                    />
                    {/* Revenue less what Telna bills. Not "margin per order":
                        that compared a price against the whole allowance and
                        made every eSIM look like a loss the day it was sold. */}
                    <StatCard
                        title="We keep"
                        value={Number(stats.revenue) - Number(stats.estimatedCostUsd)}
                        symbol={symbol}
                        tone={Number(stats.revenue) - Number(stats.estimatedCostUsd) < 0
                            ? '#cf1322'
                            : '#389e0d'}
                    />
                    {/* The float on the Telna balance. Allocated, not spent —
                        shown apart from the bill so the two are never read as
                        one number. */}
                    <StatCard
                        title="Committed on SIM wallets"
                        value={`$${Number(stats.committedUsd).toFixed(2)}`}
                        raw
                    />
                </Flex>
            ) : null}

            <CustomCard>
                <Flex gap={12} wrap style={{ marginBottom: 16 }}>
                    <Input
                        placeholder="Order number"
                        prefix={<SearchOutlined />}
                        allowClear
                        style={{ maxWidth: 220 }}
                        onChange={(e) => setQuery(e.target.value)}
                        onPressEnter={load}
                    />
                    <Select
                        placeholder="Any status"
                        allowClear
                        style={{ minWidth: 180 }}
                        value={status}
                        onChange={setStatus}
                        options={[
                            { value: PAID_STATES, label: 'Payment completed' },
                            { value: 'paid', label: 'Paid — not yet delivered' },
                            { value: 'partial', label: 'Partly delivered' },
                            { value: 'completed', label: 'Delivered' },
                            { value: 'pending_payment', label: 'Awaiting payment' },
                            { value: 'failed', label: 'Payment failed' },
                            { value: 'refunded', label: 'Refunded' },
                            { value: 'cancelled', label: 'Cancelled' },
                        ]}
                    />
                    <Select
                        placeholder="Any kind"
                        allowClear
                        style={{ minWidth: 170 }}
                        value={kind}
                        onChange={setKind}
                        options={[
                            { value: 'esim', label: 'eSIM' },
                            { value: 'wallet_topup', label: 'Wallet credit' },
                        ]}
                    />
                </Flex>

                <Spin spinning={loading}>
                    <Table
                        rowKey="orderNumber"
                        dataSource={orders}
                        columns={columns}
                        size="small"
                        scroll={{ x: 'max-content' }}
                        pagination={{ pageSize: 25, showSizeChanger: false }}
                        locale={{
                            emptyText: (
                                <EmptyOrders
                                    filtered={Boolean(status || kind || query.trim())}
                                    onClear={() => {
                                        setStatus(null);
                                        setKind(null);
                                        setQuery('');
                                    }}
                                />
                            ),
                        }}
                    />
                </Spin>
            </CustomCard>

            <Modal
                open={Boolean(selected)}
                onCancel={() => setSelected(null)}
                title={selected?.orderNumber}
                width={720}
                footer={[
                    <Button key="close" onClick={() => setSelected(null)}>Close</Button>,
                    selected?.status === 'paid' && (
                        <Button
                            key="refund"
                            danger
                            loading={refunding}
                            onClick={() => confirmRefund(selected)}
                        >
                            Refund to wallet
                        </Button>
                    ),
                ].filter(Boolean)}
            >
                {selected && (
                    <Descriptions column={2} size="small" bordered>
                        <Descriptions.Item label="Status">
                            <Tag color={statusOf(selected.status).colour}>
                                {statusOf(selected.status).label}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Placed">
                            {dateTime(selected.createdAt)}
                        </Descriptions.Item>

                        <Descriptions.Item label="Destination">
                            {selected.countryName || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Data">
                            {selected.mb ? data(selected.mb) : '—'}
                        </Descriptions.Item>

                        <Descriptions.Item label="Network">
                            {selected.networkName || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Provider">
                            {selected.provider || '—'}
                        </Descriptions.Item>

                        <Descriptions.Item label="Charged">
                            {money(selected.price, selected.currencySymbol)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Cost">
                            {/* Cost really IS dollars: it is what Telna's
                                rate card bills in, and the only figure on
                                this page that is not in the store currency. */}
                            {money(selected.costUsd, '$')}
                        </Descriptions.Item>

                        <Descriptions.Item label="From credit">
                            {money(selected.walletApplied, selected.currencySymbol)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Paid by card">
                            {money(selected.amountDue, selected.currencySymbol)}
                        </Descriptions.Item>

                        <Descriptions.Item label="Customer">
                            {selected.email || (selected.userId ? 'Account' : 'Guest')}
                        </Descriptions.Item>
                        <Descriptions.Item label="Partner app">
                            {selected.configId}
                        </Descriptions.Item>

                        <Descriptions.Item label="eSIM" span={2}>
                            {!selected.line && <Text type="secondary">Not claimed yet</Text>}
                            {selected.line && (
                                <Space direction="vertical" size={2}>
                                    <Text>
                                        {selected.line.status}
                                        {' · '}
                                        {data(selected.line.usedMb)} used of {data(selected.line.totalMb)}
                                    </Text>
                                    <Text type="secondary">
                                        {selected.line.hasActivationCode
                                            ? 'Delivered — the activation code is in the customer’s app'
                                            : 'Being prepared — no activation code yet'}
                                    </Text>
                                </Space>
                            )}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </Space>
    );
}

/**
 * One figure, sized to share its row.
 *
 * The style goes on a wrapping div rather than on `CustomCard`: the card
 * merges a caller's style now, but a plain div is the thing actually being
 * flexed and keeps the intent obvious at the call site.
 */
function StatCard({ title, value, symbol, tone, raw }) {
    return (
        <div style={{ flex: '1 1 180px', minWidth: 180 }}>
            <CustomCard>
                <Statistic
                    title={title}
                    value={value}
                    // `raw` prints the string as given — "122 MB of 200 MB"
                    // and dollar figures are already formatted, and antd
                    // would otherwise try to read them as a number.
                    // `formatter` rather than `prefix` for money: antd puts a
                    // gap after a prefix, so a price reads "€ 0.00".
                    formatter={raw
                        ? () => value
                        : (symbol ? () => money(value, symbol) : undefined)}
                    valueStyle={tone ? { color: tone } : undefined}
                />
            </CustomCard>
        </div>
    );
}

/**
 * What to say when the table is empty.
 *
 * "No orders yet." is true and useless. An operator looking at this page for
 * the first time needs to know whether something is broken or whether nothing
 * has simply been sold — and those are different sentences.
 */
function EmptyOrders({ filtered, onClear }) {
    if (filtered) {
        return (
            <div style={{ padding: '28px 12px' }}>
                <Paragraph style={{ marginBottom: 8 }}>
                    No order matches those filters.
                </Paragraph>
                <Button size="small" onClick={onClear}>Clear filters</Button>
            </div>
        );
    }

    return (
        <div style={{ padding: '32px 12px', maxWidth: 520, margin: '0 auto' }}>
            <Paragraph strong style={{ marginBottom: 6 }}>
                No orders yet — and nothing is wrong.
            </Paragraph>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Orders appear here the moment someone buys in the YouGuide One
                app or on /new-esims. Every sale records the price it was sold
                at, what it cost us and the margin between them, so a
                destination that starts losing money shows up the same day.
            </Paragraph>
        </div>
    );
}
